import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'

interface StudentRecord {
  student_iid?: number
  iid?: number
  class_roll?: number | string
  student_name_en?: string
  father_name_en?: string
  mother_name_en?: string
  student_name_bn?: string
  group?: string
  mobile?: number | string
  ssc_roll?: number | string
  ssc_board?: string
  ssc_year?: number | string
  reg_no?: number | string
  subject_code?: string
  optional_subject_code?: string
  session?: string
  gender?: string
  comment?: string
  subject_1?: string
  subject_2?: string
  subject_3?: string
  subject_4?: string
  student_photo_url?: string
  combine_subject_colm?: string
  blood_group?: string
  status?: string
  fulldata_ok?: string
  full_data_ok?: string
  [key: string]: any
}

export default function DataListSubjectPage() {
  const [allRows, setAllRows] = useState<StudentRecord[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [statusMsg, setStatusMsg] = useState<string>('Loading all records...')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Filters
  const [selectedSession, setSelectedSession] = useState<string>('')
  const [selectedGroup, setSelectedGroup] = useState<string>('')
  const [subjectField, setSubjectField] = useState<string>('all')
  const [subjectSearch, setSubjectSearch] = useState<string>('')
  const [appliedSearch, setAppliedSearch] = useState<{ field: string; query: string }>({
    field: 'all',
    query: ''
  })

  // Photo Hover Preview state
  const [hoveredPhoto, setHoveredPhoto] = useState<string | null>(null)


  // Edit Modal State
  const [editingRecord, setEditingRecord] = useState<StudentRecord | null>(null)
  const [editFormData, setEditFormData] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState<boolean>(false)
  const [modalNotice, setModalNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  // View Modal State
  const [viewingRecord, setViewingRecord] = useState<StudentRecord | null>(null)

  // Fetch all rows from Supabase
  const loadData = async () => {
    setLoading(true)
    setErrorMsg(null)
    setStatusMsg('Loading all data...')

    try {
      // Get current logged-in user email
      const { data: authData } = await supabase.auth.getUser()
      const userEmail = authData.user?.email
      let collegeEiin: number | string | null = null

      if (userEmail) {
        const { data: colUser } = await supabase
          .from('college_user')
          .select('college_eiin')
          .ilike('admin_email', userEmail.trim().toLowerCase())
          .limit(1)
          .maybeSingle()
        if (colUser?.college_eiin) {
          collegeEiin = colUser.college_eiin
        }
      }

      const batchSize = 1000
      let fetchedRows: StudentRecord[] = []
      let lastId = 0
      let round = 0
      let pkCol = 'student_iid'

      while (true) {
        round++
        setStatusMsg(`Loading... fetched ${fetchedRows.length} rows (batch ${round})`)

        let q = supabase.from('database_fgc').select('*')
        if (collegeEiin) {
          q = q.eq('college_eiin', collegeEiin)
        }
        if (lastId > 0) q = q.gt(pkCol, lastId)
        q = q.order(pkCol, { ascending: true }).limit(batchSize)

        let res = await q

        if (res.error && pkCol === 'student_iid') {
          pkCol = 'iid'
          q = supabase.from('database_fgc').select('*')
          if (collegeEiin) {
            q = q.eq('college_eiin', collegeEiin)
          }
          if (lastId > 0) q = q.gt(pkCol, lastId)
          q = q.order(pkCol, { ascending: true }).limit(batchSize)
          res = await q
        }

        if (res.error) {
          // Fallback query to database_fgc
          const fallback = await supabase.from('database_fgc').select('*').limit(2000)
          if (!fallback.error && fallback.data) {
            fetchedRows = fallback.data
            break
          }
          throw new Error(res.error.message)
        }

        const data = res.data || []
        if (data.length === 0) break

        fetchedRows = fetchedRows.concat(data)
        const lastRow = data[data.length - 1]
        const nextId = Number(lastRow.student_iid ?? lastRow.iid ?? 0)
        if (!nextId || nextId === lastId) break
        lastId = nextId

        if (data.length < batchSize) break
      }

      // Client-side sort by class_roll
      fetchedRows.sort((a, b) => (Number(a.class_roll) || 0) - (Number(b.class_roll) || 0))
      setAllRows(fetchedRows)
      setStatusMsg(`Total: ${fetchedRows.length} records shown`)
    } catch (err: any) {
      console.error('DataListSubjectPage loadData error:', err)
      setErrorMsg(err?.message || 'Failed to load data from Supabase')
      setStatusMsg('Data loading error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Dynamic Sessions List
  const availableSessions = useMemo(() => {
    const set = new Set<string>()
    allRows.forEach(r => {
      if (r.session) set.add(String(r.session).trim())
    })
    return Array.from(set).sort()
  }, [allRows])

  // Dynamic Groups List (filtered by current session)
  const availableGroups = useMemo(() => {
    const set = new Set<string>()
    allRows.forEach(r => {
      if (!r.group) return
      if (selectedSession && String(r.session || '').trim() !== selectedSession) return
      set.add(String(r.group).trim())
    })
    return Array.from(set).sort()
  }, [allRows, selectedSession])

  // Reset group filter if selected group is not valid for session
  useEffect(() => {
    if (selectedGroup && !availableGroups.includes(selectedGroup)) {
      setSelectedGroup('')
    }
  }, [selectedSession, availableGroups, selectedGroup])

  // Filtered dataset
  const filteredRows = useMemo(() => {
    const q = appliedSearch.query.trim().toLowerCase()
    const field = appliedSearch.field

    let rows = allRows.filter(r => {
      if (selectedSession && String(r.session || '').trim() !== selectedSession) return false
      if (selectedGroup && String(r.group || '').trim() !== selectedGroup) return false

      if (q) {
        if (field === 'all') {
          return [r.subject_1, r.subject_2, r.subject_3, r.subject_4].some(val =>
            String(val || '').toLowerCase().includes(q)
          )
        }
        return String(r[field] || '').toLowerCase().includes(q)
      }
      return true
    })

    return rows.sort((a, b) => (Number(a.class_roll) || 0) - (Number(b.class_roll) || 0))
  }, [allRows, selectedSession, selectedGroup, appliedSearch])

  // Execute Search
  const handleSearch = () => {
    setAppliedSearch({
      field: subjectField,
      query: subjectSearch
    })
  }

  // Clear Filters
  const handleClear = () => {
    setSelectedSession('')
    setSelectedGroup('')
    setSubjectField('all')
    setSubjectSearch('')
    setAppliedSearch({ field: 'all', query: '' })
  }

  // Highlight matching text in subject cell
  const renderSubjectCell = (value?: string, fieldName?: string) => {
    const text = value || ''
    const query = appliedSearch.query.trim()

    if (!query) return text

    const isTargetField = appliedSearch.field === 'all' || appliedSearch.field === fieldName
    if (!isTargetField) return text

    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const matchIndex = lowerText.indexOf(lowerQuery)

    if (matchIndex === -1) return text

    const before = text.slice(0, matchIndex)
    const matched = text.slice(matchIndex, matchIndex + query.length)
    const after = text.slice(matchIndex + query.length)

    return (
      <>
        {before}
        <mark style={{ backgroundColor: '#fef08a', color: '#000', padding: '1px 3px', borderRadius: '3px' }}>
          {matched}
        </mark>
        {after}
      </>
    )
  }

  // Open Edit Modal
  const handleOpenEdit = (rec: StudentRecord) => {
    setEditingRecord(rec)
    setEditFormData({ ...rec })
    setModalNotice(null)
  }

  // Save Edit Modal
  const handleSaveEdit = async () => {
    if (!editingRecord) return
    const id = editingRecord.student_iid ?? editingRecord.iid
    if (!id) return

    setSaving(true)
    setModalNotice(null)

    const payload = { ...editFormData }
    delete payload.student_iid
    delete payload.iid

    try {
      let res = await supabase.from('database_fgc').update(payload).eq('student_iid', id)
      if (res.error) {
        res = await supabase.from('database_fgc').update(payload).eq('iid', id)
      }

      if (res.error) throw res.error

      setModalNotice({ type: 'ok', text: 'Updated successfully!' })
      await loadData()
      setTimeout(() => {
        setEditingRecord(null)
      }, 600)
    } catch (err: any) {
      console.error('handleSaveEdit error:', err)
      setModalNotice({ type: 'err', text: 'Update error: ' + (err.message || 'Failed to update record') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.page}>
      {/* Top Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={styles.headerTitle}>Student List ALL</h1>
        </div>
        <Link to="/datalist" style={styles.btnAllFields}>
          All data fields ↗
        </Link>
      </header>

      {/* Main Container */}
      <div style={styles.container}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Session:</label>
            <select
              style={styles.select}
              value={selectedSession}
              onChange={e => setSelectedSession(e.target.value)}
            >
              <option value="">All Sessions</option>
              {availableSessions.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <label style={styles.filterLabel}>Group:</label>
            <select
              style={styles.select}
              value={selectedGroup}
              onChange={e => setSelectedGroup(e.target.value)}
            >
              <option value="">All Groups</option>
              {availableGroups.map(g => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>

            <label style={styles.filterLabel}>Subject Field:</label>
            <select
              style={styles.select}
              value={subjectField}
              onChange={e => setSubjectField(e.target.value)}
            >
              <option value="all">All Subjects</option>
              <option value="subject_1">Subject 1</option>
              <option value="subject_2">Subject 2</option>
              <option value="subject_3">Subject 3</option>
              <option value="subject_4">Subject 4</option>
            </select>

            <input
              type="text"
              placeholder="Search subject..."
              style={styles.searchInput}
              value={subjectSearch}
              onChange={e => setSubjectSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />

            <button style={styles.btnOutline} onClick={handleSearch}>
              Search
            </button>
            <button style={styles.btnOutline} onClick={handleClear}>
              Clear
            </button>
          </div>

          <div style={styles.rightActions}>
            <button style={styles.btnRefresh} onClick={loadData} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <span style={styles.statusText}>{statusMsg}</span>
            <span style={styles.sessionInfoText}>Table: database_fgc</span>
          </div>
        </div>

        {/* Error Notification if any */}
        {errorMsg && (
          <div style={styles.errorBanner}>
            <strong>Error:</strong> {errorMsg}
          </div>
        )}

        {/* Data Table Container */}
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>SL</th>
                <th style={styles.th}>Group</th>
                <th style={styles.th}>Roll</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Father</th>
                <th style={styles.th}>Mobile</th>
                <th style={styles.th}>Subject 1</th>
                <th style={styles.th}>Subject 2</th>
                <th style={styles.th}>Subject 3</th>
                <th style={styles.th}>Subject 4</th>
                <th style={styles.th}>Photo</th>
                <th style={{ ...styles.th, minWidth: '100px', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                    Loading data from database...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                    No matching records found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, idx) => {
                  const isFullOk = String(r.fulldata_ok || r.full_data_ok || '').toLowerCase() === 'ok'
                  const isTC = String(r.status || '').toUpperCase().includes('TC')

                  let rowStyle: React.CSSProperties = {}
                  if (isFullOk) {
                    rowStyle = { backgroundColor: '#10b981', color: '#ffffff' }
                  } else if (isTC) {
                    rowStyle = { backgroundColor: '#ff4d4f', color: '#ffffff' }
                  }

                  const idKey = r.student_iid ?? r.iid ?? idx

                  return (
                    <tr
                      key={idKey}
                      style={{
                        ...rowStyle,
                        borderBottom: '1px solid #d0d7de'
                      }}
                      className="subject-data-row"
                    >
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: 600 }}>{idx + 1}</td>
                      <td style={styles.td}>{r.group || ''}</td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>{r.class_roll || ''}</td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>{r.student_name_en || ''}</td>
                      <td style={styles.td}>{r.father_name_en || ''}</td>
                      <td style={styles.td}>{r.mobile || ''}</td>
                      <td style={styles.td}>{renderSubjectCell(r.subject_1, 'subject_1')}</td>
                      <td style={styles.td}>{renderSubjectCell(r.subject_2, 'subject_2')}</td>
                      <td style={styles.td}>{renderSubjectCell(r.subject_3, 'subject_3')}</td>
                      <td style={styles.td}>{renderSubjectCell(r.subject_4, 'subject_4')}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        {r.student_photo_url ? (
                          <img
                            src={r.student_photo_url}
                            alt="Photo"
                            style={styles.rowThumb}
                            onMouseEnter={() => {
                              setHoveredPhoto(r.student_photo_url || null)
                            }}
                            onMouseLeave={() => setHoveredPhoto(null)}
                          />
                        ) : (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>No Image</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button style={styles.btnEdit} onClick={() => handleOpenEdit(r)}>
                          Edit
                        </button>
                        <button style={styles.btnView} onClick={() => setViewingRecord(r)}>
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Row Count Footer */}
        <div style={styles.rowCount}>
          {appliedSearch.query ? (
            <span>
              {filteredRows.length} rows — searching "{appliedSearch.query}" (
              {appliedSearch.field === 'all'
                ? 'All Subjects'
                : appliedSearch.field.replace('_', ' ')}
              )
            </span>
          ) : (
            <span>{filteredRows.length} rows shown</span>
          )}
        </div>
      </div>

      {/* Floating Hover Photo Preview */}
      {hoveredPhoto && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            border: '2px solid #d0d7de',
            borderRadius: '12px',
            padding: '12px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
            zIndex: 10000,
            pointerEvents: 'none'
          }}
        >
          <img
            src={hoveredPhoto}
            alt="Preview"
            style={{
              maxWidth: '440px',
              maxHeight: '520px',
              display: 'block',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        </div>
      )}

      {/* Edit Record Modal */}
      {editingRecord && (
        <div style={styles.modalOverlay} onClick={() => setEditingRecord(null)}>
          <div style={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Edit Record</h3>

            <div style={styles.fieldsScroll}>
              {Object.keys(editFormData)
                .sort()
                .map(key => {
                  const val = editFormData[key] ?? ''
                  const isReadOnly = key === 'student_iid' || key === 'iid' || key === 'IID'
                  const isLongText =
                    String(val).length > 100 ||
                    key.toLowerCase().includes('address') ||
                    key.toLowerCase().includes('comment')

                  return (
                    <div key={key} style={styles.fieldRow}>
                      <label style={styles.fieldLabel}>{key}:</label>
                      {isLongText ? (
                        <textarea
                          rows={3}
                          style={styles.fieldInput}
                          value={val}
                          disabled={isReadOnly}
                          onChange={e => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        />
                      ) : (
                        <input
                          type="text"
                          style={{
                            ...styles.fieldInput,
                            background: isReadOnly ? '#f1f5f9' : '#fff'
                          }}
                          value={val}
                          readOnly={isReadOnly}
                          onChange={e => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        />
                      )}
                    </div>
                  )
                })}
            </div>

            {modalNotice && (
              <div
                style={{
                  ...styles.modalNotice,
                  color: modalNotice.type === 'ok' ? '#16a34a' : '#d73a49'
                }}
              >
                {modalNotice.text}
              </div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.btnCancel} onClick={() => setEditingRecord(null)}>
                Cancel
              </button>
              <button style={styles.btnSave} onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Record Modal */}
      {viewingRecord && (
        <div style={styles.modalOverlay} onClick={() => setViewingRecord(null)}>
          <div style={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>View Record</h3>
              <button style={styles.btnCloseX} onClick={() => setViewingRecord(null)}>
                ×
              </button>
            </div>

            <div style={styles.fieldsScroll}>
              {Object.keys(viewingRecord)
                .sort()
                .map(key => (
                  <div key={key} style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>{key}:</label>
                    <div style={styles.fieldViewContainer}>
                      {viewingRecord[key] === null || viewingRecord[key] === undefined
                        ? ''
                        : String(viewingRecord[key])}
                    </div>
                  </div>
                ))}
            </div>

            <div style={{ textAlign: 'right', marginTop: '16px' }}>
              <button style={styles.btnCancel} onClick={() => setViewingRecord(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    background: '#f6f8fa',
    minHeight: '100vh',
    color: '#24292f'
  },
  header: {
    background: '#0d1117',
    color: '#ffffff',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerTitle: {
    fontSize: '20px',
    margin: 0,
    fontWeight: 600
  },
  btnAllFields: {
    background: '#ff8a00',
    color: '#ffffff',
    padding: '8px 14px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid #ff8a00'
  },
  container: {
    width: '100%',
    padding: '18px 16px 40px',
    boxSizing: 'border-box'
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px',
    background: '#ffffff',
    padding: '10px 14px',
    border: '1px solid #d0d7de',
    borderRadius: '6px',
    overflowX: 'auto'
  },
  filterGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#24292f',
    whiteSpace: 'nowrap'
  },
  select: {
    padding: '6px 10px',
    border: '1px solid #d0d7de',
    borderRadius: '4px',
    fontSize: '13px',
    background: '#ffffff',
    outline: 'none',
    minWidth: '120px'
  },
  searchInput: {
    padding: '6px 10px',
    border: '1px solid #d0d7de',
    borderRadius: '4px',
    fontSize: '13px',
    width: '160px',
    outline: 'none'
  },
  btnOutline: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #0366d6',
    background: '#ffffff',
    color: '#0366d6',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  rightActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  btnRefresh: {
    padding: '7px 14px',
    borderRadius: '4px',
    border: '1px solid #0366d6',
    background: '#0366d6',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  statusText: {
    fontSize: '13px',
    color: '#1a7f37',
    fontWeight: 600
  },
  sessionInfoText: {
    fontSize: '12px',
    color: '#6a737d'
  },
  errorBanner: {
    padding: '12px 16px',
    background: '#ffebe9',
    color: '#cf222e',
    border: '1px solid rgba(255, 129, 130, 0.4)',
    borderRadius: '6px',
    marginBottom: '14px',
    fontSize: '13px'
  },
  tableWrap: {
    overflowX: 'auto',
    background: '#ffffff',
    border: '1px solid #d0d7de'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13.5px'
  },
  th: {
    border: '1px solid #d0d7de',
    padding: '8px 10px',
    textAlign: 'left',
    verticalAlign: 'middle',
    fontWeight: 600,
    fontSize: '12.5px',
    background: '#f0f3f6',
    color: '#24292f'
  },
  td: {
    border: '1px solid #d0d7de',
    padding: '7px 10px',
    textAlign: 'left',
    verticalAlign: 'middle'
  },
  rowThumb: {
    width: '40px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #d0d7de',
    background: '#ffffff',
    cursor: 'pointer',
    display: 'inline-block'
  },
  btnEdit: {
    padding: '5px 10px',
    marginRight: '6px',
    borderRadius: '4px',
    border: '1px solid #ff8a00',
    background: '#ff8a00',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  btnView: {
    padding: '5px 10px',
    borderRadius: '4px',
    border: '1px solid #0366d6',
    background: '#0366d6',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  rowCount: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#6a737d'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '60px 20px 40px',
    zIndex: 200,
    overflowY: 'auto'
  },
  modalPanel: {
    background: '#ffffff',
    width: '880px',
    maxWidth: '100%',
    border: '1px solid #d0d7de',
    borderRadius: '6px',
    padding: '20px 24px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
  },
  modalTitle: {
    margin: '0 0 16px',
    fontSize: '18px',
    fontWeight: 600
  },
  fieldsScroll: {
    maxHeight: '65vh',
    overflowY: 'auto',
    paddingRight: '6px'
  },
  fieldRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '10px',
    alignItems: 'center'
  },
  fieldLabel: {
    minWidth: '150px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#57606a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  fieldInput: {
    flex: 1,
    padding: '8px 10px',
    fontSize: '13px',
    border: '1px solid #d0d7de',
    borderRadius: '4px',
    outline: 'none'
  },
  fieldViewContainer: {
    flex: 1,
    padding: '8px 10px',
    background: '#f9f9f9',
    border: '1px solid #eee',
    borderRadius: '4px',
    fontSize: '13px',
    whiteSpace: 'pre-wrap'
  },
  modalNotice: {
    marginTop: '12px',
    fontSize: '13px',
    fontWeight: 600
  },
  modalActions: {
    textAlign: 'right',
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  btnCancel: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: '1px solid #d0d7de',
    background: '#f6f8fa',
    color: '#24292f',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  btnSave: {
    padding: '8px 18px',
    borderRadius: '4px',
    border: '1px solid #0366d6',
    background: '#0366d6',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  btnCloseX: {
    background: 'transparent',
    border: 'none',
    fontSize: '22px',
    color: '#64748b',
    cursor: 'pointer'
  }
}
